import { Chip, IconButton, Tooltip, useTheme } from '@mui/material';
import { useMountEffect } from '@react-hookz/web';
import { useDebouncer } from '@tanstack/react-pacer/debouncer';
import { type Message, generateId } from 'ai';
import _ from 'lodash';
import React from 'react';
import TablerIconHistory from '~icons/tabler/history';
import TablerIconMaximize from '~icons/tabler/maximize';
import TablerIconPlus from '~icons/tabler/plus';
import TablerIconX from '~icons/tabler/x';
import type { APIResponse } from '#/api';
import { useLayout } from '#/layouts';
import {
  type NightingaleEventData,
  type NightingaleEventName,
  useNightingale,
} from '#/lib/nightingale';
import { cn, styleVars } from '#/utils/styles';
import ChatBox from './chat-box';
import ChatErrorBoundary from './chat-error-boundary';
import ChatHistoryMenu from './chat-history-menu';
import ChatHistoryPanel from './chat-history-panel';
import ChatMessage from './chat-message';
import ChatMessageError from './chat-message-error';
import ChatMessagesErrorBoundary from './chat-messages-error-boundary';
import { formatContextForAPI } from './context-data';
import type { ChatContextItem } from './types';
import { useChat } from './use-chat';

function ChatPanel() {
  const [, layoutActions] = useLayout();
  const [isFullScreen, setIsFullScreen] = React.useState(false);
  const latestMessageRef = React.useRef<HTMLDivElement>(null);
  const [chatId, setChatId] = React.useState<string>();
  const nightingale = useNightingale();
  const [chatHistoryAnchorEl, setChatHistoryAnchorEl] =
    React.useState<HTMLButtonElement | null>(null);
  const [previousChat, setPreviousChat] = React.useState<PreviousChat | null>();
  const [context, setContext] = React.useState<ChatContextItem[]>([]);
  const chat = useChat({
    model: 'gpt-4.1', // this is not used right now
    context,
    id: chatId,
    initialMessages: previousChat?.messages as Message[],
  });

  /*
  |------------------
  | Computed
  |------------------
  */

  const title = React.useMemo(() => {
    const foundTitle = _.chain(chat.data ?? [])
      .findLast((d) => _.has(d, 'title'))
      .value();
    return foundTitle?.title ?? previousChat?.title ?? 'New Chat';
  }, [previousChat, chat.data]);

  const latestUserMessage = React.useMemo(() => {
    return _.chain(chat.messages)
      .findLast((message) => message.role === 'user')
      .value();
  }, [chat.messages]);

  const nightingaleCapture = React.useCallback(
    (event: NightingaleEventName, data: NightingaleEventData = {}) => {
      nightingale.capture(event, {
        chatId: chat.id,
        chatTitle: title,
        chatMessages: chat.messages,
        ...data,
      });
    },
    [nightingale, title, chat]
  );

  /*
  |------------------
  | Callbacks
  |------------------
  */

  const handleFullScreenChange = React.useCallback(
    (isFullScreen: boolean) => {
      nightingaleCapture(['engage', 'ai_chat'], {
        type: 'full_screen',
      });
      setIsFullScreen(isFullScreen);
    },
    [nightingaleCapture]
  );

  const handleNewChat = React.useCallback(() => {
    const newId = generateId();
    nightingaleCapture(['view', 'ai_chat'], {
      chatId: newId,
      chatTitle: 'New Chat',
      chatMessages: [],
      type: 'chat',
    });
    setChatId(newId);
    setContext([]);
    setPreviousChat(null);
  }, [nightingaleCapture]);

  const handleSelectPreviousChat = React.useCallback(
    (chat: PreviousChat) => {
      nightingaleCapture(['view', 'ai_chat'], {
        chatId: chat.chatId,
        chatTitle: chat.title,
        chatMessages: chat.messages,
        type: 'chat_history',
      });
      setPreviousChat(chat);
      setContext([
        ...(chat.context?.githubRepositories ?? []),
        ...(chat.context?.githubRepositoryFiles ?? []),
      ]);
      setChatId(chat.chatId);
    },
    [nightingaleCapture]
  );

  const handleContextChange = React.useCallback(
    (context: ChatContextItem[]) => {
      setContext(context);
      nightingaleCapture(['engage', 'ai_chat'], {
        type: 'context',
      });
    },
    [nightingaleCapture]
  );
  const handleStop = React.useCallback(() => {
    nightingaleCapture(['cancel', 'ai_chat']);
    chat.stop();
  }, [chat.stop, nightingaleCapture]);

  const handleSubmit = React.useCallback(() => {
    nightingaleCapture(['submit', 'ai_chat']);
    chat.append({
      role: 'user',
      content: chat.input,
      annotations: [
        {
          type: 'context',
          context: formatContextForAPI(context),
        },
      ],
    });
    chat.setInput('');
  }, [chat.append, nightingaleCapture, context, chat.input, chat.setInput]);

  const handleChange = React.useCallback(
    (
      e:
        | React.ChangeEvent<HTMLInputElement>
        | React.ChangeEvent<HTMLTextAreaElement>
    ) => {
      chat.handleInputChange(e);
    },
    [chat.handleInputChange]
  );

  const scrollToLatestMessage = useDebouncer(
    () => {
      if (latestMessageRef.current) {
        latestMessageRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    },
    {
      wait: 100,
    }
  );

  /*
  |------------------
  | Effects
  |------------------
  */

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only on message change
  React.useEffect(() => {
    scrollToLatestMessage.maybeExecute();
  }, [chat.messages]);

  useMountEffect(() => {
    nightingaleCapture(['view', 'ai_chat']);
  });

  return (
    <ChatContainer fullScreen={isFullScreen}>
      <ChatHeader>
        <div className='overflow-hidden text-ellipsis whitespace-nowrap'>
          <Chip label={title} size='small' shape='rounded' />
        </div>
        <div className='flex-1' />
        <Tooltip title='Start a new chat'>
          <IconButton size='small' onClick={handleNewChat}>
            <TablerIconPlus />
          </IconButton>
        </Tooltip>
        <span>
          <Tooltip title={'View past chats'}>
            <span>
              <IconButton
                size='small'
                onClick={(e) => {
                  nightingaleCapture(['engage', 'ai_chat'], {
                    type: 'chat_history',
                  });
                  setChatHistoryAnchorEl(e.currentTarget);
                }}
              >
                <TablerIconHistory />
              </IconButton>
            </span>
          </Tooltip>
          <ChatHistoryMenu
            currentChatId={chatId}
            open={!!chatHistoryAnchorEl}
            onClose={() => setChatHistoryAnchorEl(null)}
            anchorEl={chatHistoryAnchorEl}
            onSelect={handleSelectPreviousChat}
          />
        </span>
        <Tooltip title={isFullScreen ? 'Exit full screen' : 'Close the chat'}>
          <IconButton
            size='small'
            onClick={() => {
              if (isFullScreen) {
                handleFullScreenChange(false);
              } else {
                layoutActions.closeUtilityPanel();
              }
            }}
          >
            <TablerIconX />
          </IconButton>
        </Tooltip>
        {!isFullScreen && (
          <Tooltip title='Full screen'>
            <IconButton
              size='small'
              onClick={() => handleFullScreenChange(true)}
            >
              <TablerIconMaximize />
            </IconButton>
          </Tooltip>
        )}
      </ChatHeader>
      <ChatBody fullScreen={isFullScreen}>
        <ChatMessagesErrorBoundary>
          {chat.messages.length > 0 && (
            <ChatMessages>
              {chat.messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  inputMessage={latestUserMessage}
                  message={message}
                  status={chat.status}
                  onStop={handleStop}
                />
              ))}
              {chat.status === 'error' && (
                <ChatMessageError
                  onRetry={() => {
                    chat.reload();
                  }}
                />
              )}
              {/* {!intent.id && 'working...'}
              {intent && intent.confidence} */}
              {/* Only used for scroll to bottom */}
              <div ref={latestMessageRef} />
            </ChatMessages>
          )}
        </ChatMessagesErrorBoundary>
        <ChatInput>
          <ChatBox
            context={context}
            status={chat.status}
            input={chat.input}
            onInputChange={handleChange}
            onSubmit={handleSubmit}
            onContextChange={handleContextChange}
          />
        </ChatInput>
        {chat.messages.length === 0 && (
          <ChatHistoryPanel onSelect={handleSelectPreviousChat} />
        )}
      </ChatBody>
    </ChatContainer>
  );
}

export default () => (
  <ChatErrorBoundary>
    <ChatPanel />
  </ChatErrorBoundary>
);

/*
|------------------
| Internal
|------------------
*/

type PreviousChat = APIResponse<'GET /_prv/ai/chats'>[number];

function ChatContainer({
  children,
  fullScreen,
}: { children: React.ReactNode; fullScreen: boolean }) {
  const theme = useTheme();
  return (
    <div
      className={cn('flex flex-col', {
        'fixed inset-0 z-(--chat-fs-z) bg-background-default h-full':
          fullScreen,
        'h-(--chat-height)': !fullScreen,
      })}
      style={styleVars({
        '--chat-height': `calc(100vh - ${theme.measurements.topBar.height}px)`,
        '--chat-fs-z': (theme.zIndex.appBar + 1).toString(),
      })}
    >
      {children}
    </div>
  );
}

function ChatHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className='flex items-center justify-between p-4'>{children}</div>
  );
}

function ChatBody({
  children,
  fullScreen,
}: { children: React.ReactNode; fullScreen: boolean }) {
  return (
    <div
      className={cn('flex-1 flex flex-col overflow-hidden w-full', {
        'mx-auto max-w-full md:max-w-[800px] lg:max-w-[900px]': fullScreen,
      })}
    >
      {children}
    </div>
  );
}

function ChatMessages({ children }: { children: React.ReactNode }) {
  return <div className='flex-1 overflow-y-auto p-4 space-y-4'>{children}</div>;
}

function ChatInput({ children }: { children: React.ReactNode }) {
  return (
    <div className='flex flex-col items-center space-y-4 space-x-2'>
      <div className='p-4 h-full w-full'>{children}</div>
    </div>
  );
}
